import { useEffect, useRef, useState } from 'react';

interface CounterProps {
  end: number;
  duration: number;
  suffix?: string;
}

function Counter({ end, duration, suffix = '%' }: CounterProps) {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const counterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isVisible) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.5 }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return;
    }

    let startTime: number;
    let animationFrame: number;

    const animate = (currentTime: number) => {
      if (!startTime) {
        startTime = currentTime;
      }
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * end);
      setCount(currentCount);
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [isVisible, end, duration]);

  return (
    <div ref={counterRef} className="text-6xl font-bold text-gray-900 mb-4 font-['Times_New_Roman']">
      {count}
      {suffix}
    </div>
  );
}

export default function PerformanceSection() {
  return (
    <section className="py-24 bg-white">
      <div className="landing-container">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="text-center">
            <Counter end={6} duration={2000} suffix="x" />
            <p className="text-lg text-gray-600 leading-relaxed">
              Aumento de até 6x no volume de interações por post.
            </p>
          </div>
          <div className="text-center">
            <Counter end={100} duration={2000} suffix="%" />
            <p className="text-lg text-gray-600 leading-relaxed">
              Crescimento de até 100% na média de impressões por Story no Instagram.
            </p>
          </div>
          <div className="text-center">
            <Counter end={75} duration={2000} suffix="%" />
            <p className="text-lg text-gray-600 leading-relaxed">
              Redução de até 75% no custo por lead (CPL) nas peças geradas via Culture Monitor.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

